/**
 * Cloud Operations — Section layout with sub-navigation.
 *
 * Renders the section chrome (title, sub-nav, footer notes) and an <Outlet/>
 * for the active sub-route. Each sub-route is its own page component.
 *
 * Routes (per design doc §2 IA):
 *   /cloud-operations/overview        — KPI grid + recent runs
 *   /cloud-operations/deployments     — list (W2.x)
 *   /cloud-operations/cloud-accounts  — list + create (this PR)
 *   /cloud-operations/targets         — deployment targets (this PR — stub list)
 *   /cloud-operations/workflows       — System Deployment Workflow catalog (this PR)
 *   /cloud-operations/bridges         — bridge agents (W2.x stub)
 *   /cloud-operations/sla             — SLA matrix (W2.x)
 *   /cloud-operations/audit           — operations log (W2.x)
 *
 * RBAC: Platform Admin (cross-customer) + Super Admin (customer-scoped via
 * backend service-layer filter). The App.jsx route guard admits both at the
 * coarse level (minRole="Super Admin" = ROLE_LEVELS[5], which Platform Admin
 * also satisfies); per-resource filtering happens in the backend services.
 *
 * Memory: feedback_v2_inline_over_modal — anchored dropdowns/slideouts only.
 * Memory: UI display conventions — pagination on every list, human-readable names.
 *
 * NOTE on file naming: renamed from OperatorsConsoleLayout.jsx during W2.x to
 * match the user-facing "Cloud Operations" surface. The old filename keeps a
 * thin re-export shim for forward-compat.
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { NavLink, Outlet } from 'react-router-dom';

const TINTS = {
  ink:        '#0a1d44',
  ink2:       '#0e3552',
  text:       '#1a1f2b',
  text2:      '#5b6b7e',
  text3:      '#8896a8',
  stroke:     '#d4dbe5',
  stroke2:    '#e8edf3',
  bgCard:     '#ffffff',
  bgTint:     '#f7f9fc',
  bgAlt:      '#eef2f8',
  action:     '#0e3552',
};

const TABS = [
  { to: 'overview',       label: 'Overview' },
  { to: 'deployments',    label: 'Deployments' },
  { to: 'targets',        label: 'Targets' },
  { to: 'cloud-accounts', label: 'Cloud accounts' },
  { to: 'workflows',      label: 'System workflows' },
  { to: 'bridges',        label: 'Bridge agents' },
  { to: 'sla',            label: 'SLA matrix' },
  { to: 'audit',          label: 'Operations log' },
];

export default function CloudOperationsLayout() {
  return (
    <div style={{
      padding: '20px 32px 32px',
      maxWidth: 1400,
      margin: '0 auto',
      color: TINTS.text,
    }}>
      <Header />
      <SubNav />
      <div style={{ minHeight: 400 }}>
        <Outlet />
      </div>
    </div>
  );
}

function Header() {
  // Reflect the actual role in the chrome rather than hard-coding "PA only".
  // PA sees cross-customer; SA sees customer-scoped data.
  const { user } = useAuth() || {};
  const role = user?.role || user?.currentRole;
  const scopeLine = role === 'Platform Admin'
    ? 'Platform · cross-customer view'
    : role === 'Super Admin'
      ? 'Customer-scoped view'
      : 'Platform · PA + Super Admin';
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: TINTS.text3, letterSpacing: '.04em', marginBottom: 2 }}>
        {scopeLine}
      </div>
      <h1 style={{ margin: 0, fontSize: 26, color: TINTS.ink, fontWeight: 700 }}>
        Cloud Operations
      </h1>
      <div style={{ color: TINTS.text2, fontSize: 13, marginTop: 4 }}>
        Single-pane control plane for every Prove7 deployment — Prove7 Cloud and Self-Hosted, across all supported clouds.
        Platform Admin sees cross-customer; Super Admin sees their customer's deployments only.
      </div>
    </div>
  );
}

function SubNav() {
  return (
    <div style={{
      display: 'flex',
      gap: 2,
      borderBottom: `1px solid ${TINTS.stroke}`,
      marginBottom: 24,
      flexWrap: 'wrap',
    }}>
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          style={({ isActive }) => ({
            padding: '10px 16px',
            fontSize: 13,
            fontWeight: 600,
            color: isActive ? TINTS.ink : TINTS.text2,
            textDecoration: 'none',
            borderBottom: `2px solid ${isActive ? TINTS.action : 'transparent'}`,
            marginBottom: -1,
            transition: 'color .12s',
          })}
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}
